
import { Vue, Component, Watch } from "vue-property-decorator";
import { routePaths } from "@/router";
import { inject } from "inversify-props";
import { Guid } from "guid-typescript";
import {
  ISiteService,
  IMealCountService,
  IMealCountStatusService,
  IAuthService,
  ISyncService
} from "@/services";
import {
  AddressCardModel,
  MealCountKey,
  MealCountDto,
  DashboardCountStatus,
  ProgramType} from "@/models";
import { eventBus, mcEvents, mcDate, mcString, mcLogger } from "@/utils";
import { mcUserMessages, mcLabels, MealCountDtoViewModel } from "@/view-models";
import { types } from "@/store";
import moment from "moment";

@Component
export default class Dashboard extends Vue {
  @inject() private readonly siteService!: ISiteService;
  @inject() private readonly mealCountService!: IMealCountService;
  @inject() private readonly mealCountStatusService!: IMealCountStatusService;
  @inject() private readonly authService!: IAuthService;
  @inject() private readonly syncService!: ISyncService;

  private readonly console: Console = mcLogger.getConsole(
    this.constructor.name,
    mcLogger.colors.Dashboard
  );

  noAccess = false;
  loading = false;

  /** Used to set the enabled dates for the date picker */
  enabledDates: string[] = [];

  /** Used to set the minimum start date for the date picker */
  operationStartDate = "";

  /** Used to set the maximum end date for the date picker */
  operationEndDate = "";

  mealDate = "";
  selectedSiteId: number | null = null;
  selectedSponsorId = null;
  selectedProgramId = null;

  timeStamp = 0;
  timeout: number = 130 * 60 * 1000;

  selectedSite = new AddressCardModel();
  selectedSponsor = new AddressCardModel();

  noContentMessage: string | null = mcUserMessages.dashboard.needSelections;
  mealTypes: string[] = [];
  countStatuses = this.mealCountStatusService.getDefaultStatuses();

  created() {
    if (this.$store.getters[types.state.isAuthenticated]) {
      this.activateApp();
    } else {
      eventBus.$on(mcEvents.loggedIn, this.activateApp);
    }

    eventBus.$on(mcEvents.selectionsCleared, this.loadFromMemory);
    eventBus.$on(mcEvents.receiveUpdatedCount, this.onReceiveUpdatedCount);
  }

  beforeDestroy() {
    eventBus.$off(mcEvents.loggedIn, this.activateApp);
    eventBus.$off(mcEvents.selectionsCleared, this.loadFromMemory);
    eventBus.$off(mcEvents.receiveUpdatedCount, this.onReceiveUpdatedCount);

    if (this.selectedSiteId) {
      eventBus.$emit(mcEvents.deselectedDashboardSite, this.selectedSiteId?.toString());
    }
  }

  @Watch("selectedSiteId")
  handleSelectedSiteId(newVal, oldVal) {
    const flags = this.$store.getters[types.state.featureFlags];

    if (oldVal) {
      const oldSiteFlags = flags.find(m => m.siteId == oldVal);

      // TODO: remove the msl check after signalR use is vetted in Prod.
      // signalR is useful on the dashboard for single lines too.
      if (oldSiteFlags.msl.isActive) {
        eventBus.$emit(mcEvents.deselectedDashboardSite, oldVal.toString());
      }
    }

    if (newVal) {
      const newSiteFlags = flags.find(m => m.siteId == newVal);

      // TODO: remove the msl check after signalR use is vetted in Prod.
      // signalR is useful on the dashboard for single lines too.
      if (newSiteFlags.msl.isActive) {
        eventBus.$emit(mcEvents.selectedDashboardSite, newVal.toString());
      }
    }
  }

  /** Process the received SignalR message. */
  async onReceiveUpdatedCount(mealCountKey:MealCountKey) {
    if (this.selectedSiteId == mealCountKey.siteId
      && this.mealDate === mcDate.toShortDate(mealCountKey.mealServiceDate)) {
        await this.syncService.syncMealsDown([mealCountKey]);
        this.updateCountStatuses();
    }
  }

  /**
   * If possible, using the information in the store, set the selected values in the sponsor, site, program, and meal date drop-downs.
   * If the user had a meal count they were previously working on, prompt them to load that meal count or to discard it.
   */
  loadFromMemory() {
    this.mealDate = "";

    const storedSponsorId = this.$store.getters[types.state.selectedSponsorId];
    const storedSiteId = this.$store.getters[types.state.selectedSiteId];
    const storedProgramId = this.$store.getters[types.state.selectedProgramId];
    const storedMealDate = this.$store.getters[types.state.selectedMealDate];

    if (storedSponsorId !== 0) {
      this.updateSelectedSponsor(storedSponsorId);
    }

    if (storedSiteId !== 0) {
      this.siteSelected(storedSiteId);
    } else {
      this.selectedSiteId = null;
      this.selectedSite.clear();
    }

    if (storedProgramId !== "") {
      this.programSelected(storedProgramId);
    } else {
      this.programSelected(ProgramType.SFSP);
    }

    if (storedMealDate !== "") {
      this.dateSelected();
      this.mealDate = storedMealDate;
    }

    const user = this.$store.state.auth.user;
    const tempCounts = this.$store.getters[types.state.tempDto];
    const tempCount = tempCounts?.length > 0 ? tempCounts[0] : null;
    if (
      tempCount &&
      tempCount.mealTypeId &&
      tempCount.mealServiceDate &&
      tempCount.siteId > 0 &&
      tempCount.receivedMeals.receivedBy === user.userId
    ) {
      this.showTempCountModal(tempCount);
    }
  }

  /** Request the drop-down info to be loaded, then toggle the loading flag off */
  async activateApp() {
    await this.loadSites();
    this.loading = false;
  }

  /**
   * Display a modal prompting the user to continue with their previous meal count or to discard them
   * @param tempCount The information of their previous meal count
   */
  showTempCountModal(tempCount: MealCountDto) {
    const vm = new MealCountDtoViewModel(tempCount);

    this.$bvModal
      .msgBoxConfirm(
        mcLabels.tempCountText(
          !tempCount.isNonCongregate ? tempCount.mealTypeId : "Non-Congregate",
          tempCount.mealServiceDate,
          vm.siteName
        ),
        {
          title: mcLabels.modalTitles.tempCount,
          okTitle: "CONTINUE",
          cancelTitle: "DISCARD",
          cancelVariant: "discard",
          footerClass: "p-2",
          hideHeaderClose: false
        }
      )
      .then(value => {
        if (value) {
          this.goToTempCount(vm);
        } else {
          this.$store.dispatch(types.actions.setTempDto, {});
        }
      })
      .catch(err => {
        console.log("error: ", err);
      });
  }

  /**
   * Redirect the user to the meal count they were previously working on
   * @param vm Contains the meal count information, used to route to the user to the correct location
   */
  goToTempCount(vm: MealCountDtoViewModel) {
    const tempCount = vm.mealCounts;
    const date = mcDate.toShortDate(tempCount.mealServiceDate);
    const query = this.getRouteQueryParams(
      tempCount.siteId,
      vm.sponsorId,
      tempCount.programId,
      date,
      !tempCount.isNonCongregate ? tempCount.mealTypeId.toUpperCase() : "NON_CONGREGATE",
      "serve"
    );
    this.$router.push({
      name: routePaths.serveMeals.name,
      params: query
    });
  }

  getRouteQueryParams(siteId, sponsorId, programId, date, mealType, type) {
    return {
      siteId: siteId,
      sponsorId: sponsorId,
      programId: programId,
      date: date,
      mealType: mealType,
      type: type
    };
  }

  get isIdle() {
    return this.$store.state.idleVue.isIdle;
  }

  @Watch("$route", { immediate: true, deep: true })
  @Watch("isIdle", { immediate: true, deep: true })
  clearDashboard() {
    const storedTimeStamp = this.$store.getters[types.state.timeStamp];
    if (storedTimeStamp !== 0) {
      const timePassed = new Date().getTime() - storedTimeStamp;
      if (timePassed >= this.timeout) {
        eventBus.$emit(mcEvents.sessionTimedOut);
        this.loadSites();
        this.loading = false;
      }
    }
  }

  @Watch("selectionsReady", { immediate: true, deep: true })
  setTimeStamp() {
    if (this.selectionsReady) {
      this.timeStamp = new Date().getTime();
      this.$store.dispatch(types.actions.setTimeStamp, this.timeStamp);
    }
  }

  /**
   * Populate the Sponsors, Sites, and Program drop-downs.
   * If there are no available sponsors, display the No Access warning message instead.
   */
  async loadSites() {
    this.loading = true;
    await this.siteService.activate();
    this.loadFromMemory();

    if (!this.moreThanOneSponsor) {
      const sponsor = this.$store.getters[types.state.sponsors][0];
      if (sponsor) {
        this.updateSelectedSponsor(sponsor.sponsorId);
      } else {
        this.noAccess = true;
      }
    }

    if (!this.moreThanOneAvailableSite) {
      const site = this.$store.getters[types.state.sites][0];
      if (site) {
        this.selectedSiteId = site.siteId;
        this.siteSelected(site.siteId);
      }
    }

    if (!this.moreThanOneAvailableProgram) {
      const program = this.$store.getters[types.state.programs].find(
        p => p.isActive
      );
      this.selectedProgramId = program.programId;
      this.programSelected(program.programId);
    }
  }

  get moreThanOneSponsor() {
    return this.$store.getters[types.state.sponsors].length > 1;
  }

  get moreThanOneAvailableSite() {
    return this.$store.getters[types.state.sites].length > 1;
  }

  get moreThanOneAvailableProgram() {
    return (
      this.$store.getters[types.state.programs].filter(p => p.isActive).length >
      1
    );
  }

  get isSponsorSelected() {
    return !!this.selectedSponsorId;
  }

  get sponsorOptions() {
    const sponsors = this.$store.getters[types.state.sponsors];
    return [
      {
        sponsorId: null,
        sponsorName: "Please select a sponsor",
        disabled: true
      }
    ].concat(sponsors);
  }

  get siteOptions() {
    const emptySite = [
      {
        siteId: null,
        siteName: this.isSponsorSelected
          ? "Please select a site"
          : "Sponsor must be selected",
        disabled: true
      }
    ];
    let result = emptySite;

    if (this.isSponsorSelected) {
      const sites = this.$store.state.counter.sites;
      const sponsorSites = sites.filter(
        s => s.sponsorId === this.selectedSponsorId
      );
      result = emptySite.concat(sponsorSites);
    }
    return result;
  }

  get programOptions() {
    const programs = this.$store.state.counter.programs;
    return [
      {
        programId: null,
        programAbbreviation: "Please select a program",
        programDescription: null,
        disabled: true
      }
    ].concat(programs);
  }

  @Watch("mealDate")
  async dateSelected() {
    this.updateMealTypes();
    await this.updateCountStatuses();
    this.$store.dispatch(types.actions.setSelectedMealDate, this.mealDate);
  }

  async sponsorSelected(sponsorId) {
    this.selectedSite.clear();
    const sponsor = this.$store.state.counter.sponsors.find(
      x => x.sponsorId === sponsorId
    );

    if (sponsor) {
      this.selectedSponsor.loadFromSponsor(sponsor);
      if (this.moreThanOneSponsor) {
        this.selectedSiteId = null;
        this.mealDate = "";
      }
    } else {
      this.selectedSponsor.clear();
      this.selectedSite.clear();
    }

    if (!sponsorId) {
      this.clearEnabledDates();
    }

    this.updateMealTypes();
    await this.updateCountStatuses();
    this.updateEnabledDates();
    this.$store.dispatch(types.actions.setSelectedSponsorId, sponsorId);
  }

  async siteSelected(siteId) {
    this.selectedSiteId = siteId;
    const state = this.$store.getters[types.state.sites];
    const site = state.find(x => x.siteId === siteId);
    if (site) {
      this.selectedSite.loadFromSite(site);
    } else {
      this.selectedSiteId = null;
      this.selectedSite.clear();
    }

    if (!siteId) {
      this.clearEnabledDates();
    }

    this.updateMealTypes();
    await this.updateCountStatuses();
    this.updateEnabledDates();
    this.$store.dispatch(types.actions.setSelectedSiteId, siteId);
  }

  async programSelected(programId) {
    this.selectedProgramId = programId;
    const state = this.$store.state.counter.programs;
    const program = state.find(p => p.programId === programId);
    if (program) {
      this.selectedProgramId = program.programId;
    } else {
      this.selectedProgramId = null;
    }

    if (!programId) {
      this.clearEnabledDates();
    }

    this.updateMealTypes();
    await this.updateCountStatuses();
    this.updateEnabledDates();
    this.$store.dispatch(types.actions.setSelectedProgramId, programId);
  }

  clearEnabledDates() {
    this.enabledDates = [""];
  }

  get selectionsReady() {
    return (
      this.mealDate &&
      this.mealDate !== "" &&
      this.selectedSiteId &&
      this.selectedProgramId &&
      this.selectedSponsorId
    );
  }

  /** Gets the available meal types for the selected day from appInfo */
  updateMealTypes() {
    if (this.selectionsReady) {
      const appInfo = this.$store.getters[types.state.siteAppDates];

      const targetSiteId: number = this.selectedSiteId || -1; // Have to have '|| -1' or else it will complain that you can't have null for an index value
      const targetProgramId: string = this.selectedProgramId || ""; // Stops linting error about null index values
      const targetDate = moment(this.mealDate);
      const targetYear: number = targetDate.year();
      const targetDayOfYear: number = targetDate.dayOfYear();

      const siteAppInfos = appInfo.filter(
        o => o.siteId === targetSiteId && o.programId === targetProgramId
      );

      let found = false;
      if (siteAppInfos.length > 0) {
        for (const siteAppInfo of siteAppInfos) {
          const mealTypes = siteAppInfo.years
            .filter(
              y =>
                y.year === targetYear && y.days.some(d => d.day === targetDayOfYear)
            )
            .map(y => mcString.toTitleCase(y.mealTypeId));

          if (mealTypes.length > 0) {
            this.mealTypes = mealTypes;
            found = true;
          }
        }
      }

      let apps = siteAppInfos.filter(siteAppInfo => siteAppInfo.years.some(y => y.year === targetYear && y.days.some(d => d.day === targetDayOfYear)))
                                .map(siteAppInfo => siteAppInfo.programYearId)
        .filter(this.onlyUnique);
      const nonCongregateMeals = this.$store.state.counter.siteAppNonCongregateMeals.find(nc => nc.siteId === targetSiteId &&
                                                                                              nc.programId === targetProgramId &&
                                                                                              nc.programYearId === apps[0]);

      // TODO: Check if day of the week is non-congregate eligible
      const isNonCongregateEligible = nonCongregateMeals?.nonCongregateMealApprovedInd;
      if(isNonCongregateEligible && !this.mealTypes.includes("Non_Congregate")) {
        this.mealTypes.push("Non_Congregate");
        found = true;
      }

      if (!found) {
        this.mealTypes = [];
      }

      this.noContentMessage = null;
      if (this.mealTypes.length === 0) {
        this.noContentMessage = mcUserMessages.dashboard.noSiteAppMeals;
      }
    } else {
      this.mealTypes = [];
      this.noContentMessage = mcUserMessages.dashboard.needSelections;
    }

    this.sortMealTypes();
  }

  onlyUnique(value, index, array) {
    return array.indexOf(value) === index;
  }

  /** Defines the sort order for the Meal Types */
  sortMealTypes() {
    const mealTypeSortOrder = [
      "Non_Congregate",
      "Breakfast",
      "Snack_am",
      "Lunch",
      "Snack_pm",
      "Supper"
    ];

    this.mealTypes.sort(function(a, b) {
      return mealTypeSortOrder.indexOf(a) - mealTypeSortOrder.indexOf(b);
    });
  }

  async updateCountStatuses() {
    if (
      this.mealDate &&
      this.mealDate !== "" &&
      this.selectedSiteId &&
      this.selectedProgramId
    ) {
      const keys = this.mealCountStatusService.buildDailyKeys(
        this.mealTypes.filter(mt => mt !== "Non_Congregate"),
        this.selectedSiteId,
        this.mealDate
      );
      const statuses = await this.mealCountStatusService.buildCountStatuses(
        keys
      );
      this.countStatuses = statuses;
    }
  }

  /**
   * Populate this.enabledDates with valid dates for the selected Program, Sponsor, and Site.
   * Uses appInfo to build this data by recontructing the condensed dates.
   */
  updateEnabledDates() {
    this.clearEnabledDates();

    // Don't bother getting the dates until all the options have been chosen
    if (
      this.selectedSponsorId &&
      this.selectedSiteId &&
      this.selectedProgramId
    ) {
      const appInfo = this.$store.getters[types.state.siteAppDates];
      const targetSiteId = this.selectedSiteId || -1; // Stops linting error about null index values
      const targetProgramId = this.selectedProgramId || ""; // Stops linting error about null index values

      const siteAppInfos = appInfo.filter(
        o => o.siteId === targetSiteId && o.programId === targetProgramId
      );

      if (siteAppInfos.length > 0) {
        for (const siteAppInfo of siteAppInfos) {
          var startDate = mcDate.toShortDate(siteAppInfo.operationStartDate);
          var endDate = mcDate.toShortDate(siteAppInfo.operationEndDate);

          if (
            this.operationStartDate === "" ||
            moment(startDate).isBefore(moment(this.operationStartDate))
          ) {
            this.operationStartDate = startDate;
          }

          if (
            this.operationEndDate === "" ||
            moment(endDate).isAfter(moment(this.operationEndDate))
          ) {
            this.operationEndDate = endDate;
          }

          for (const year of siteAppInfo.years) {
            for (const dayOfYear of year.days) {
              const validDate = moment()
                .year(year.year)
                .dayOfYear(dayOfYear.day); // Reconstruct a valid day using the year and day of year values
              const shortDate = mcDate.toShortDate(validDate);
              this.enabledDates.push(shortDate);
            }
          }
        }
      }
      if (
        !this.disableNow &&
        (this.mealDate == null ||
          this.mealDate === "" ||
          !this.enabledDates.includes(this.mealDate))
      ) {
        this.mealDate = mcDate.shortDateNow();
      } else if (this.enabledDates.includes(this.mealDate)) {
        return this.mealDate;
      } else {
        this.mealDate = "";
      }
    }
  }

  get disableNow() {
    return (
      this.enabledDates.length > 0 &&
      !this.enabledDates.includes(mcDate.shortDateNow())
    );
  }

  updateSelectedSponsor(sponsorId) {
    this.selectedSponsorId = sponsorId;
    this.sponsorSelected(sponsorId);
  }

  getMealCount(mealTypeId: string): MealCountDtoViewModel {
    const mealType = mealTypeId.toUpperCase();
    let result = this.mealCounts.find(mc => mc.mealTypeId === mealType);
    if (!result) {
      result = new MealCountDto(
        Guid.create().toString(),
        this.selectedSiteId!,
        this.selectedProgramId!,
        mealType,
        this.mealDate
      );
    }
    return new MealCountDtoViewModel(result);
  }

  get mealCounts(): MealCountDto[] {
    let result: MealCountDto[] = [];

    if (this.selectionsReady) {
      const keys = this.mealTypes.map(
        mealTypeId =>
          new MealCountKey(
            this.selectedSiteId!,
            this.selectedProgramId!,
            mealTypeId,
            this.mealDate
          )
      );
      result = this.mealCountService.findMealCounts(keys);
    }

    return result;
  }

  getMealStatus(mealTypeId: string): DashboardCountStatus {
    return this.countStatuses[mealTypeId.toLowerCase()];
  }

  async logout() {
    if (navigator.onLine) {
      this.authService.startLogout();
    } else {
      await this.authService.offlineLogout();
    }
  }
}
